import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAdBannerQuery } from "@/components/Banner/AdBanner";
import { PreviewDialogProvider } from "@/components/Search/PreviewDialogProvider";
import { EditionCategoryGridSection, EditionCategoryHeader, EditionCategoryPageLayout, EditionProductSection, MobileCategoryDrawer, MobileFilterSheet, ProductFilter } from "@/components/edition/category";
import { ITEMS_PER_PAGE, QUERY_KEYS } from "@/components/edition/category/constants";
import { useCategoryProducts, useCategoryUrlState, useEditionCategoryData, useMobileUI } from "@/components/edition/category/hooks";
import { EDITION_CATEGORY_PAGE } from "@/settings/constant";
import { Typography } from "@bookips/sds";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
const EditionCategoryPage = ()=>{
    const router = useRouter();
    const queryClient = useQueryClient();
    // URL 상태 관리 (필터, 정렬, 페이지)
    const { selectedCategoryId , currentPage , selectedSort , selectedFilters , handleSortChange , handleFilterChange , handleFilterReset , handlePageChange  } = useCategoryUrlState({
        reservedParams: [
            "editionId",
            "id",
            "page",
            "sort"
        ],
        defaultSort: "최신순"
    });
    const editionId = router.query.editionId;
    // 모바일 UI 상태
    const { isMobileFilterOpen , mobileFilterExpandedKey , isMobileCategoryDrawerOpen , openMobileFilter , closeMobileFilter , openMobileCategoryDrawer , closeMobileCategoryDrawer  } = useMobileUI();
    // 카테고리 데이터 (API + 파생값 + 사이드이펙트)
    const { categoryInfo , isLoading , isCategoryError , firstCategoryId , lnbItems , lnbTitle , isFirstDepth , isLastDepthByTree , directChildCategories , categoryPath  } = useEditionCategoryData(editionId, selectedCategoryId);
    // 상품 데이터
    const productsQuery = useCategoryProducts({
        editionId,
        categoryId: selectedCategoryId,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        sort: selectedSort,
        filters: {
            units: selectedFilters.units,
            dataTypes: selectedFilters.dataTypes,
            authors: selectedFilters.authors,
            steps: selectedFilters.steps
        },
        isLastDepthByTree,
        isCategoryLoading: isLoading
    });
    // 배너 데이터
    const { adBannerResponse  } = useAdBannerQuery({
        position: "editionCategory",
        pageId: typeof editionId === "string" ? editionId : undefined
    });
    const banners = useMemo(()=>{
        return (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items.map((item)=>({
                id: item.id,
                imageUrl: item.imageUrl,
                imageUrlPc: item.imageUrlPc,
                imageUrlTablet: item.imageUrlTablet,
                imageUrlMobile: item.imageUrlMobile,
                link: item.link
            }))) || [];
    }, [
        adBannerResponse
    ]);
    // 네비게이션 핸들러
    const handleCategorySelect = useCallback((categoryId)=>{
        if (categoryId === selectedCategoryId) {
            queryClient.invalidateQueries({
                queryKey: [
                    QUERY_KEYS.CATEGORY_PRODUCTS
                ]
            });
            return;
        }
        router.push("".concat(EDITION_CATEGORY_PAGE, "/").concat(editionId, "?id=").concat(categoryId));
    }, [
        selectedCategoryId,
        queryClient,
        router,
        editionId
    ]);
    const getCategoryHref = useCallback((item)=>"".concat(EDITION_CATEGORY_PAGE, "/").concat(editionId, "?id=").concat(item.categoryId), [
        editionId
    ]);
    const handleDepthCategoryChange = useCallback((id)=>{
        router.push("".concat(EDITION_CATEGORY_PAGE, "/").concat(id));
    }, [
        router
    ]);
    const handleViewAll = useCallback(()=>{
        if (firstCategoryId) handleCategorySelect(firstCategoryId);
    }, [
        firstCategoryId,
        handleCategorySelect
    ]);
    // 레이아웃 props
    const layoutProps = useMemo(()=>({
            lnbTitle,
            lnbTitleHref: "".concat(EDITION_CATEGORY_PAGE, "/").concat(editionId),
            lnbItems,
            selectedCategoryId,
            onCategorySelect: handleCategorySelect,
            onDepthCategoryChange: handleDepthCategoryChange,
            categoryInfo,
            isLoading,
            onMobileCategoryDrawerOpen: openMobileCategoryDrawer,
            isFirstDepth
        }), [
        lnbTitle,
        editionId,
        lnbItems,
        selectedCategoryId,
        handleCategorySelect,
        handleDepthCategoryChange,
        categoryInfo,
        isLoading,
        openMobileCategoryDrawer,
        isFirstDepth
    ]);
    // 에러 상태
    if (isCategoryError) {
        return /*#__PURE__*/ _jsx(PreviewDialogProvider, {
            children: /*#__PURE__*/ _jsx(EditionCategoryPageLayout, {
                ...layoutProps,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "py-12 text-center",
                    children: /*#__PURE__*/ _jsx(Typography, {
                        className: "text-gray-500",
                        children: "카테고리 정보를 불러올 수 없습니다."
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(PreviewDialogProvider, {
        children: /*#__PURE__*/ _jsxs(EditionCategoryPageLayout, {
            ...layoutProps,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-6 sm:gap-8",
                    children: [
                        /*#__PURE__*/ _jsx(EditionCategoryHeader, {
                            lnbTitle: lnbTitle,
                            lnbTitleHref: "".concat(EDITION_CATEGORY_PAGE, "/").concat(editionId),
                            banners: banners,
                            categoryPath: categoryPath,
                            getCategoryHref: getCategoryHref,
                            categoryInfo: categoryInfo,
                            isLoading: isLoading,
                            isFirstDepth: isFirstDepth
                        }),
                        /*#__PURE__*/ _jsx(EditionCategoryGridSection, {
                            selectedCategoryId: selectedCategoryId,
                            isFirstDepth: isFirstDepth,
                            isLastDepthByTree: isLastDepthByTree,
                            lnbItems: lnbItems,
                            directChildCategories: directChildCategories,
                            onCategorySelect: handleCategorySelect,
                            getCategoryHref: getCategoryHref,
                            categoryInfo: categoryInfo,
                            isLoading: isLoading
                        }),
                        isLastDepthByTree && productsQuery.filterItems.length > 0 && /*#__PURE__*/ _jsx(ProductFilter, {
                            filterItems: productsQuery.filterItems,
                            selectedFilters: selectedFilters,
                            onFilterChange: handleFilterChange,
                            onReset: handleFilterReset,
                            onMobileFilterOpen: openMobileFilter,
                            isLoading: productsQuery.isFilterLoading
                        }),
                        /*#__PURE__*/ _jsx(EditionProductSection, {
                            isLastDepthByTree: isLastDepthByTree,
                            productsQuery: productsQuery,
                            selectedSort: selectedSort,
                            onSortChange: handleSortChange,
                            currentPage: currentPage,
                            onPageChange: handlePageChange,
                            purchaseUrl: categoryInfo === null || categoryInfo === void 0 ? void 0 : categoryInfo.purchaseUrl,
                            isCategoryLoading: isLoading
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(MobileFilterSheet, {
                    isOpen: isMobileFilterOpen,
                    onClose: closeMobileFilter,
                    filterItems: productsQuery.filterItems,
                    selectedFilters: selectedFilters,
                    onFilterChange: handleFilterChange,
                    initialExpandedKey: mobileFilterExpandedKey
                }),
                /*#__PURE__*/ _jsx(MobileCategoryDrawer, {
                    isOpen: isMobileCategoryDrawerOpen,
                    onClose: closeMobileCategoryDrawer,
                    title: lnbTitle,
                    items: lnbItems,
                    selectedCategoryId: selectedCategoryId,
                    onCategorySelect: handleCategorySelect,
                    onViewAll: handleViewAll
                })
            ]
        })
    });
};
export default EditionCategoryPage;
